/** Typed publish/subscribe bus. The main seam between subsystems. */

import type { EventBus, GameEvents } from './types';

type AnyHandler = (payload: never) => void;

class Bus implements EventBus {
  private handlers = new Map<keyof GameEvents, Set<AnyHandler>>();

  on<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(fn as AnyHandler);
    return () => this.off(type, fn);
  }

  once<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): () => void {
    const wrapped = (payload: GameEvents[K]) => {
      this.off(type, wrapped);
      fn(payload);
    };
    return this.on(type, wrapped);
  }

  /**
   * Removal is immediate, including from inside a dispatch. `emit` iterates a
   * copy, so deleting from the live set mid-dispatch is safe — and deferring it
   * was not: `once` unsubscribes itself from inside its own handler, so with a
   * deferred removal any event re-emitted during that dispatch (a handler that
   * toasts, which emits, whose handler emits again) still found the wrapper in
   * the set and ran it a second time.
   */
  off<K extends keyof GameEvents>(type: K, fn: (payload: GameEvents[K]) => void): void {
    this.handlers.get(type)?.delete(fn as AnyHandler);
  }

  emit<K extends keyof GameEvents>(type: K, payload: GameEvents[K]): void {
    const set = this.handlers.get(type);
    if (!set || set.size === 0) return;
    // Copy so handlers may subscribe or unsubscribe during dispatch: a listener
    // added now must not be called for the event that added it, and one removed
    // now has already been captured. The single-handler case is the common one
    // and needs no array at all.
    if (set.size === 1) {
      const only = set.values().next().value as ((p: GameEvents[K]) => void) | undefined;
      if (only) this.call(type, only, payload);
      return;
    }
    for (const fn of Array.from(set)) this.call(type, fn as (p: GameEvents[K]) => void, payload);
  }

  private call<K extends keyof GameEvents>(type: K, fn: (p: GameEvents[K]) => void, payload: GameEvents[K]): void {
    try {
      fn(payload);
    } catch (err) {
      console.error(`[events] handler for "${String(type)}" threw:`, err);
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}

export function makeEventBus(): EventBus {
  return new Bus();
}
